import { Analytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { User as AuthUser } from 'firebase/auth';
import { ServiceAuthType, ServiceProvider, User } from '@uploadcam/common';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { USER_AUTH_STATE_CHANGED$, USER_GET_CURRENT$ } from '../../../core/core.module';
import { getLogger, getSentry, getSentryUserErrorsUrl, isDev } from '../../../core/utils';
import { ChatwootService } from './chatwoot.service';
import { FirebaseToken } from '../../../core/firebase.module';

type PurchaseType = 'initial' | 'upgrade';

/**
 * Keys and related (optional) properties for analytics events.
 *
 * The convention is: `<context>_<action>`.
 * `context` describes the user journey or feature area (e.g., `login`, `create_team`).
 * `action` describes the specific step or outcome within that flow (e.g., `start`, `completed`, `error`).
 * The 'web_' prefix is added automatically by the track method.
 * **Keys cannot exceed 36 characters. The Firebase limit is 40chars, and we will add `web_` latter. **
 */
interface AnalyticsEvents {
  //
  //---------------------------------| Event key can't exceed this line (36 chars.)
  //
  // `bulk_invite_users_start` ─┬─▶ `bulk_invite_users_completed`
  //                            ├─▶ `bulk_invite_users_canceled`
  //                            └─▶ `bulk_invite_users_error`
  bulk_invite_users_start: { provider: ServiceProvider };
  bulk_invite_users_completed: { provider: ServiceProvider };
  bulk_invite_users_canceled: { provider: ServiceProvider };
  bulk_invite_users_error: { provider: ServiceProvider };
  // `create_album_start` ─┬─▶ `create_album_completed`
  //                       ├─▶ `create_album_canceled`
  //                       └─▶ `create_album_error`
  create_album_start: { provider: ServiceProvider };
  create_album_completed: { provider: ServiceProvider };
  create_album_canceled: { provider: ServiceProvider };
  create_album_error: { provider: ServiceProvider };
  // `create_team_start` ─┬─▶ `create_team_completed` ◀─┐
  //                      └─▶ `create_team_error` ──────┘
  create_team_start: { provider: ServiceProvider };
  create_team_completed: { provider: ServiceProvider };
  create_team_error: { provider: ServiceProvider };
  // `onboard_invite_users_start` ─┬─▶ `onboard_invite_users_completed`
  //                               ├─▶ `onboard_invite_users_skipped`
  //                               └─▶ `onboard_invite_users_error`
  onboard_invite_users_start: { provider: ServiceProvider };
  onboard_invite_users_completed: { provider: ServiceProvider };
  onboard_invite_users_skipped: { provider: ServiceProvider };
  onboard_invite_users_error: { provider: ServiceProvider };
  // `onboard_survey_start` ─┬─▶ `onboard_survey_completed`
  //                         └─▶ `onboard_survey_error`
  onboard_survey_start: undefined;
  onboard_survey_completed: undefined;
  onboard_survey_error: undefined;
  // `invite_user_start` ─┬─▶ `invite_user_completed`
  //                      ├─▶ `invite_user_canceled`
  //                      └─▶ `invite_user_error`
  invite_user_start: { provider: ServiceProvider };
  invite_user_completed: { provider: ServiceProvider };
  invite_user_canceled: { provider: ServiceProvider };
  invite_user_error: { provider: ServiceProvider };
  // `login_start` ─┬─▶ `login_completed`
  //                ├─▶ `login_canceled`
  //                ├─▶ `login_failed_post_condition`
  //                ├─▶ `login_insufficient_scopes`
  //                └─▶ `login_error`
  login_start: { authType: ServiceAuthType; mode: 'popup' | 'redirect' };
  login_canceled: { authType?: string };
  login_completed: { authType: ServiceAuthType };
  login_failed_post_condition: { authType?: string };
  login_insufficient_scopes: { authType?: string };
  login_error: { authType?: string };
  // `logout_start` ─┬─▶ `logout_completed`
  //                 └─▶ `logout_error`
  logout_start: { provider?: ServiceProvider };
  logout_completed: { provider?: ServiceProvider };
  logout_error: { provider?: ServiceProvider };
  // ●─┬─▶ `purchase_paywall_view` ───▶ `purchase_start` ─┬─▶ `purchase_completed`
  //   └─▶ `purchase_paywall_error`                       ├─▶ `purchase_canceled`
  //                                                      └─▶ `purchase_error`
  purchase_paywall_view: undefined;
  purchase_paywall_error: undefined;
  purchase_start: { priceId: string; type: PurchaseType };
  purchase_completed: { priceId: string; type: PurchaseType };
  purchase_error: { priceId?: string; type: PurchaseType };
  purchase_canceled: { priceId: string; type: PurchaseType };
  // ●───▶ `report_waiting` ───▶ `report_form` ───▶ `report_ready`
  report_waiting: undefined;
  report_form: undefined;
  report_ready: undefined;
}

type AnalyticsEvent = keyof AnalyticsEvents;
type RequiresProperties<ErrorKey extends keyof AnalyticsEvents> =
  AnalyticsEvents[ErrorKey] extends undefined ? false : true;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  /** Firebase max event/propery key lengh */
  private readonly MAX_FB_KEY_LENGTH = 40;
  private readonly logger = getLogger('AnalyticsService');

  constructor(
    @Inject(FirebaseToken.Analytics)
    private readonly analytics: Analytics,
    private readonly chatwoot: ChatwootService,
    @Inject(USER_AUTH_STATE_CHANGED$)
    private readonly userAuthStateChanged$: Observable<AuthUser | undefined>,
    @Inject(USER_GET_CURRENT$)
    private readonly userGetCurrent$: Observable<User>,
  ) {}

  public async init(): Promise<void> {
    this.userAuthStateChanged$.subscribe((user) => {
      if (user) {
        getSentry()?.setUser({ id: user.uid });
        setUserId(this.analytics, user.uid);
      } else {
        getSentry()?.setUser(null);
        setUserId(this.analytics, null);
      }
    });

    this.userGetCurrent$.subscribe((user) => {
      const { capabilities: capabilitiesRaw, lastLoginProvider, teamId } = user;
      const capabilities = capabilitiesRaw.sort().join(',');

      setUserProperties(this.analytics, {
        capabilities: this.applyFirebaseLimit(capabilities),
        provider: lastLoginProvider ?? null,
        teamId: teamId ?? null,
      });

      this.chatwoot.run((chatwoot) => {
        chatwoot.setUser(user.id, {
          email: user.email,
          name: user.fullName ?? user.displayName,
        });

        chatwoot.setCustomAttributes({
          sentry_user_errors: getSentryUserErrorsUrl(user.id),
        });
      });

      getSentry()?.setTags({
        capabilities: capabilities,
        provider: lastLoginProvider,
        teamId: teamId,
      });
    });
  }

  public clearUserTags(): void {
    setUserProperties(this.analytics, {
      capabilities: null,
      provider: null,
      teamId: null,
    });

    getSentry()?.setTags({
      capabilities: undefined,
      provider: undefined,
      teamId: undefined,
    });
  }

  public track<EventKey extends AnalyticsEvent>(
    eventKey: EventKey,
    ...properties: RequiresProperties<EventKey> extends true ? [AnalyticsEvents[EventKey]] : []
  ): void {
    let eventName = `web_${eventKey}`;
    const props = properties ? properties[0] : undefined;

    if (eventName.length > this.MAX_FB_KEY_LENGTH) {
      this.logger.warn(
        `Event name "${eventName}" exceeds ${this.MAX_FB_KEY_LENGTH} characters. Truncating.`,
      );
      eventName = this.applyFirebaseLimit(eventName);
    }

    if (isDev()) {
      this.logger.log(`Event: ${eventName}`, props);
    }

    try {
      // Track in Firebase
      logEvent(this.analytics, eventName, props);

      // Track in Sentry
      getSentry()?.addBreadcrumb({
        category: 'analytics',
        message: eventName,
        ...(props ? { data: props } : {}),
        level: 'info',
      });
    } catch (err) {
      this.logger.error(`Failed to track event "${eventName}"`, err);
    }
  }

  private applyFirebaseLimit(value: string): string {
    return value.length > this.MAX_FB_KEY_LENGTH
      ? value.substring(0, this.MAX_FB_KEY_LENGTH)
      : value;
  }
}
